import { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

// Exchange rates, base = GBP (the Swell store's native currency)
const EXCHANGE_RATES = {
  GBP: 1,
  USD: 1 / 0.79, // 1 GBP = ~1.27 USD (approximate)
};

const CURRENCY_SYMBOLS = {
  USD: '$',
  GBP: '£',
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('USD');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    detectCurrency();
  }, []);

  const detectCurrency = async () => {
    try {
      // Try to detect from browser locale first
      const userLocale = navigator.language || navigator.userLanguage;
      
      if (userLocale.startsWith('en-GB') || userLocale.startsWith('en-UK')) {
        setCurrency('GBP');
        setLoading(false);
        return;
      }

      // Try to get more accurate location from IP geolocation
      // Using a free service - you can replace with your preferred service
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      if (data.country_code === 'GB') {
        setCurrency('GBP');
      } else if (data.country_code === 'US') {
        setCurrency('USD');
      } else {
        // Default to USD for other countries
        setCurrency('USD');
      }
    } catch (error) {
      console.log('Could not detect location, defaulting to USD:', error);
      setCurrency('USD');
    } finally {
      setLoading(false);
    }
  };

  const convert = (amount, fromCurrency = 'GBP') => {
    if (fromCurrency === currency) return amount;

    // Convert to GBP first, then to target currency
    const gbpAmount = amount / EXCHANGE_RATES[fromCurrency];
    return gbpAmount * EXCHANGE_RATES[currency];
  };

  const format = (amount, fromCurrency = 'GBP') => {
    const convertedAmount = convert(amount, fromCurrency);
    const symbol = CURRENCY_SYMBOLS[currency];
    return `${symbol}${convertedAmount.toFixed(2)}`;
  };

  const value = {
    currency,
    setCurrency,
    convert,
    format,
    symbol: CURRENCY_SYMBOLS[currency],
    loading,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
